/**
 * Text Component
 *
 * A versatile text component for body copy, labels, and inline text.
 * Maps to the tokenized type scale for consistent typography.
 *
 * Features:
 * - Multiple size variants (xl, lg, base, sm, xs, label, caption)
 * - Semantic color variants (muted, strong, status colors)
 * - Polymorphic element (p, span, div, label, etc.)
 * - Truncation and line-clamp support
 * - Weight and style overrides
 *
 * @example
 * ```tsx
 * // Default paragraph
 * <Text>This is body text content.</Text>
 *
 * // Muted helper text
 * <Text variant="muted" size="sm">
 *   Additional information about the above.
 * </Text>
 *
 * // Strong/emphasized text
 * <Text variant="strong">Important note</Text>
 *
 * // Inline span with custom size
 * <Text as="span" size="xs">Label text</Text>
 *
 * // Error message
 * <Text variant="error" size="sm">Please enter a valid email.</Text>
 * ```
 *
 * @see typography.types.ts for type definitions
 * @see docs/tokens.md for token documentation
 */

import * as React from 'react';
import {cn} from '@/shared/utils/utils';
import type {TextProps} from './typography.types';
import {buildResponsiveTextSizeClasses, lineClampClasses, textVariants, textVariantsWithoutSize} from './Text.variants';

// =============================================================================
// TEXT COMPONENT
// =============================================================================

/**
 * Text component for body copy and inline text.
 */
const Text = React.memo(function Text({
  as = 'p',
  size = 'base',
  variant = 'default',
  weight,
  align = 'left',
  truncate = false,
  lineClamp,
  italic = false,
  underline = false,
  leadingTrim = false,
  className,
  children,
  'data-testid': testId,
  ...props
}: TextProps): React.ReactElement {
  // Determine the element to render
  const Tag = as;

  // Check if size is a responsive object
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isResponsiveSize = typeof size === 'object' && size !== null;
  const responsiveSizeClasses = isResponsiveSize
    ? buildResponsiveTextSizeClasses(size)
    : undefined;

  // Build className - don't include weight if variant="strong" already sets it
  const shouldIncludeWeight = weight !== undefined && variant !== 'strong';

  // Build variant props object for non-responsive sizes
  let textClassName: string;

  if (!isResponsiveSize) {
    // For non-responsive sizes, use CVA with all props including size
    // Ensure size is always a valid TextSize (should be since !isResponsiveSize)
    const textSize = size;

    // Build variant props object - pass all props directly to CVA
    textClassName = cn(
      textVariants({
        size: textSize,
        variant,
        align,
        truncate,
        italic,
        underline,
        leadingTrim,
        ...(shouldIncludeWeight ? { weight: weight as NonNullable<typeof weight> } : {}),
      }),
      lineClamp && lineClampClasses[lineClamp],
      className
    );
  } else {
    // For responsive sizes, build classes manually
    // The size is handled separately via buildResponsiveTextSizeClasses
    // Use textVariantsWithoutSize to avoid applying default size='base'
    textClassName = cn(
      responsiveSizeClasses,
      textVariantsWithoutSize({
        variant,
        align,
        truncate,
        italic,
        underline,
        leadingTrim,
        ...(shouldIncludeWeight ? { weight: weight as NonNullable<typeof weight> } : {}),
      }),
      lineClamp && lineClampClasses[lineClamp],
      className
    );
  }

  // Determine data-size attribute value
  const dataSize = isResponsiveSize
    ? `${(size).base || 'base'}-responsive`
    : size;

  return (
    <Tag
      className={textClassName}
      data-testid={testId}
      data-typography="text"
      data-size={dataSize}
      data-variant={variant}
      {...props}
    >
      {children}
    </Tag>
  );
});

Text.displayName = 'Text';

// =============================================================================
// EXPORTS
// =============================================================================

export { Text };
